import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Switch,
    Typography,
} from '@mui/material';
import * as React from 'react';

import { describeError, formatTs } from './components';
import { mirrorAccount, mirrorNeedsVendorKey, mirrorUnavailableReason, OUTBOUND_MODES } from './labels';
import type { DeveloperMirror } from './types';

const LABEL = "Mirror production's webhooks";

/** The words that depend on where the switch is: the sandbox's own Overview, or production's sandbox card (ESB). */
const WORDS = {
    sandbox: {
        about: 'While it is on, the webhooks your booking systems send to production are copied here and processed under the outbound mode above.',
        on: "Production's webhooks are copied into this sandbox, guest data included.",
        readOnly: 'Only a developer with the mirror permission can switch it.',
    },
    production: {
        about: "While it is on, the webhooks your booking systems send here are copied into the sandbox and processed under the sandbox's outbound mode.",
        on: "This account's webhooks are copied into the sandbox, guest data included.",
        readOnly: 'Only a user with the permission “Developer: sign in to the sandbox” can switch it.',
    },
};

/** "Mirror of production's webhooks" (MIRROR-TOGGLE plan v3): while it is on, bproxy copies the webhooks production's
 *  booking systems send into this sandbox. Off by default; switching on asks first, switching off applies at once.
 *  `developer.mirror_manage` switches it, everyone else reads; while not `available` it cannot be switched on, but
 *  switching off is always possible. `where="production"`: the same switch on production's sandbox card (ESB;
 *  explicit `developer.sandbox_signin` holders), the same rules and confirmation; `suspended` (the card knows it)
 *  keeps it from being switched on until the sandbox is resumed. `needs_vendor_key` (ESB-FHK): integrations whose
 *  copies the sandbox skips because it holds no vendor key while production does -- said on both sides. */
const MirrorPanel = ({
    mirror,
    canManage,
    outboundMode,
    onChange,
    where = 'sandbox',
    suspended = false,
}: {
    mirror: DeveloperMirror;
    canManage: boolean;
    outboundMode: string | null | undefined;
    onChange: (enabled: boolean) => Promise<DeveloperMirror>;
    where?: 'sandbox' | 'production';
    /** The sandbox is suspended: switching on is refused (423) until it is resumed; switching off is not. */
    suspended?: boolean;
}) => {
    const words = WORDS[where];
    const [confirming, setConfirming] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);

    const apply = async (enabled: boolean) => {
        setConfirming(false);
        setBusy(true);
        setError(null);
        try {
            await onChange(enabled);
        } catch (e) {
            setError(e);
        } finally {
            setBusy(false);
        }
    };

    const accounts = mirror.accounts ? mirror.accounts.map(mirrorAccount).join(', ') : null;
    const mode = outboundMode ? OUTBOUND_MODES[outboundMode]?.label || outboundMode : null;

    return (
        <Box data-testid="mirror-panel">
            <FormControlLabel
                control={(
                    <Switch
                        checked={mirror.enabled}
                        disabled={!canManage || busy || (!mirror.enabled && (!mirror.available || suspended))}
                        onChange={(ev) => (ev.target.checked ? setConfirming(true) : apply(false))}
                        inputProps={{ 'aria-label': LABEL }}
                    />
                )}
                label={LABEL}
            />
            <Typography variant="body2" color="text.secondary">{words.about}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }} data-testid="mirror-last-received">
                Last mirrored webhook: {mirror.last_received_at ? formatTs(mirror.last_received_at) : 'None yet'}
            </Typography>
            {accounts && (
                <Typography variant="body2" data-testid="mirror-accounts">Accounts copied: {accounts}</Typography>
            )}
            {mirror.enabled && (
                <Alert severity="warning" sx={{ mt: 1 }}>{words.on}</Alert>
            )}
            {!mirror.available && (
                <Alert severity="info" sx={{ mt: 1 }}>{mirrorUnavailableReason(mirror.unavailable_reason)}</Alert>
            )}
            {(mirror.needs_vendor_key || []).map((entry) => (
                <Alert key={entry.integration} severity="warning" sx={{ mt: 1 }} data-testid="mirror-needs-vendor-key">
                    {mirrorNeedsVendorKey(entry.integration)}
                </Alert>
            ))}
            {suspended && !mirror.enabled && mirror.available && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="mirror-suspended">
                    Resume the sandbox to switch the mirror on.
                </Typography>
            )}
            {!canManage && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{words.readOnly}</Typography>
            )}
            {error ? <Alert severity="error" sx={{ mt: 1 }}>{describeError(error)}</Alert> : null}
            <Dialog open={confirming} onClose={() => setConfirming(false)} aria-labelledby="mirror-title">
                <DialogTitle id="mirror-title">Mirror production&apos;s webhooks?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Real production webhooks, guest data included, will be copied into {where === 'sandbox' ? 'this' : 'the'} sandbox within seconds and
                        processed under the sandbox&apos;s outbound mode{mode ? ` (now: ${mode})` : ''}. The sandbox is then
                        labelled as holding production data.
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 1.5 }}>
                        {accounts
                            ? `Only these accounts' webhooks are copied (Curated Planet chose them): ${accounts}.`
                            : "Every account's webhooks are copied: Curated Planet can narrow this to some accounts."}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={() => apply(true)}>Start mirroring</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MirrorPanel;
